import {
    BuildingPricingMetricType,
    type GetBuildingCheckoutPricingPreviewResponse,
} from '@quipex/shared/contracts';
import type { PricingCalculationSummary } from './ui/models';

export function createPricingCalculationSummary(
    preview: GetBuildingCheckoutPricingPreviewResponse
): PricingCalculationSummary | null {
    if (preview.metric.type === BuildingPricingMetricType.FallbackFlatFee) {
        return null;
    }

    const metric = metricLabel(preview.metric.type);
    const unit = metricUnit(preview.metric.type);
    const min = preview.band.minMetricValue;
    const max = preview.band.maxMetricValue;

    return {
        variant: 'band',
        metricLabel: metric,
        minLabel: formatNumber(min),
        currentLabel: formatNumber(preview.metric.value),
        maxLabel:
            max === null || max === undefined
                ? 'No upper limit'
                : formatNumber(max),
        rangeLabel: `${formatBandRange(preview)} ${unit}`,
        amountLabel: `${formatCheckoutPrice(preview, preview.band.amount)} / building / year`,
        currentPositionPercent: currentBandPositionPercent(
            preview.metric.value,
            min,
            max
        ),
        metricUnit: unit,
    };
}

export function formatCheckoutPrice(
    preview: GetBuildingCheckoutPricingPreviewResponse,
    amount: number
): string {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: preview.currency,
    }).format(amount);
}

function metricLabel(
    metricType: GetBuildingCheckoutPricingPreviewResponse['metric']['type']
): string {
    if (metricType === BuildingPricingMetricType.FloorAreaSquareMetres) {
        return 'floor area';
    }

    if (metricType === BuildingPricingMetricType.ResidentialLots) {
        return 'residential lots';
    }

    return 'building size';
}

function metricUnit(
    metricType: GetBuildingCheckoutPricingPreviewResponse['metric']['type']
): string {
    if (metricType === BuildingPricingMetricType.FloorAreaSquareMetres) {
        return 'm²';
    }

    if (metricType === BuildingPricingMetricType.ResidentialLots) {
        return 'lots';
    }

    return '';
}

function formatBandRange(
    preview: GetBuildingCheckoutPricingPreviewResponse
): string {
    const min = preview.band.minMetricValue;
    const max = preview.band.maxMetricValue;

    if (max === null || max === undefined) return `${min}+`;

    return `${min}-${max}`;
}

function currentBandPositionPercent(
    metricValue: number,
    min: number,
    max: number | null | undefined
): number {
    if (max === null || max === undefined || max <= min) return 100;

    const rawPercent = ((metricValue - min) / (max - min)) * 100;

    return Math.min(Math.max(rawPercent, 0), 100);
}

function formatNumber(value: number): string {
    return new Intl.NumberFormat('en-AU').format(value);
}
